/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';

import { Box, Grid, Typography } from '@mui/material';

import { EntitiesItemNew } from '../../components/EntitiesItenNew/EntitiesItemNew';
import { EntityThumbnail } from '../../components/EntityThumbnail/EntityThumbnail';
import { ProductsList } from '../../components/ProductsList/ProductsList';
import { ProductsListItem } from '../../components/ProductsList/ProductsListItem';
import { BasicRoutes } from '../../constants/basicRoutes';
import { Namespaces } from '../../i18n';
import { RootState } from '../../store';
import { EntityId } from '../../types/api/EntityId';
import { ProductStatus } from '../../types/product/ProductStatus';
import { prepareRoutePath } from '../../utils/prepareRoutePath';
import { fetchDashboardForUser } from '../../utils/queryFns';
import { useControlOverlays } from '../../utils/useControlOverlays';
import { useDocumentTitle } from '../../utils/useDocumentTitle';
import { useFavoriteServices } from '../../utils/useFavoriteServices';
import { Chart } from './components/Chart/Chart';
import { FavoriteServices } from './components/FavoriteServices';
import { createHomeStyles } from './HomePage.styles';

const mapStateToProps = (state: RootState) => ({
  auth: state.auth,
});
const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
type IProps = PropsFromRedux;

const HomePageComponent = memo<IProps>(props => {
  const classes = createHomeStyles();
  const { t } = useTranslation([Namespaces.Common, Namespaces.OrganizationAdmin]);
  useDocumentTitle(t('page_titles.home'));

  const { auth } = props;
  const navigate = useNavigate();
  const name = auth.user?.firstName;

  const isSuperuser = auth.user?.isSuperuser;

  const { doOpenCreateOrganizationOverlay } = useControlOverlays();

  const handleOrganizationClick = (organization: { id: string }) => {
    void navigate(
      prepareRoutePath(BasicRoutes.ORGANIZATION_PAGE, {
        ':organization_id': organization.id,
      }),
    );
  };

  const dashboardQuery = useQuery([fetchDashboardForUser.key], fetchDashboardForUser.fn);
  const isFetching = dashboardQuery.status === 'loading';

  const organizations = useMemo(() => dashboardQuery.data?.data?.payload?.organizations ?? [], [dashboardQuery.data]);
  const { favoriteServices } = useFavoriteServices();

  const services = useMemo(
    () =>
      favoriteServices.sort(
        (a, b) =>
          a.organization.name.toLowerCase().localeCompare(b.organization.name.toLowerCase()) ||
          a.project.name.toLowerCase().localeCompare(b.project.name.toLowerCase()) ||
          a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
      ),
    [favoriteServices],
  );

  const products = useMemo(() => {
    const statistics: ProductStatus = {
      Running: 0,
      Creating: 0,
      Updating: 0,
      Importing: 0,
      Unavailable: 0,
      Error: 0,
    };
    if (!isFetching && organizations) {
      organizations.forEach(project => {
        project.products.forEach(product => {
          (Object.keys(statistics) as Array<keyof ProductStatus>).forEach(key => {
            statistics[key] = statistics[key] + (product[key] ?? 0);
          });
        });
      });
    }
    return statistics;
  }, [isFetching, organizations]);

  const organizationProducts = (organizationId: EntityId) => {
    const statistics: ProductStatus = {
      Running: 0,
      Creating: 0,
      Updating: 0,
      Importing: 0,
      Unavailable: 0,
      Error: 0,
    };

    if (!isFetching && organizations) {
      organizations
        .find(org => org.id === organizationId)
        ?.products.forEach(product => {
          (Object.keys(statistics) as Array<keyof ProductStatus>).forEach(key => {
            statistics[key] = statistics[key] + (product[key] ?? 0);
          });
        });
    }
    return statistics;
  };

  const showNewItem = useMemo(() => {
    if (isSuperuser) {
      return true;
    } else {
      return !organizations.length;
    }
  }, [isSuperuser, organizations.length]);

  return (
    <Grid container gap={2}>
      <Typography classes={{ h1: classes.title }} variant="h1" data-cy="home-user-greeting">
        {t('home.user_greeting', { userName: name })}
      </Typography>

      <Grid container spacing={2}>
        <Grid item display="flex" width="100%" height="300px" gap={2} alignItems="stretch">
          <Box width="460px" height="100%">
            <Chart
              productsStatistic={products}
              isLoading={isFetching}
              hideEmptyStatuses={true}
              style={{ height: '300px' }}
            />
          </Box>
          <Box width="500px" display="flex" alignItems="stretch">
            <FavoriteServices
              width="100%"
              height="281px"
              organizationsDisplayed={organizations.length !== 1}
              user={auth.user!}
              services={services}
              isLoading={isFetching}
            />
          </Box>
        </Grid>
      </Grid>
      <Grid item my={3}>
        <ProductsList
          title={t('home.your_organisations')}
          loading={isFetching}
          isEmpty={!organizations.length}
          noItemsTitle={t('home.no_organizations')}
          numberOfSkeletons={2}
        >
          <>
            {organizations.map(organization => (
              <ProductsListItem
                key={organization.id}
                name={organization.name}
                products={organizationProducts(organization.id)}
                icon={
                  <EntityThumbnail
                    size={24}
                    type="organization"
                    organizationId={organization.id}
                    allowUploadImage={false}
                  />
                }
                onClick={() => handleOrganizationClick(organization)}
                noItemsText={t(`${Namespaces.OrganizationAdmin}:departments.products.no_products_in_organization`)}
              />
            ))}
            {showNewItem && (
              <EntitiesItemNew
                title={t(`${Namespaces.Entities}:organization.create`)}
                onClick={doOpenCreateOrganizationOverlay}
                disabled={!isSuperuser}
                disabledTitle={t('home.no_organizations_annotation')}
              />
            )}
          </>
        </ProductsList>
      </Grid>
    </Grid>
  );
});

export const HomePage = connector(HomePageComponent);
